import { getLogger } from '@/logger';
import { Event } from '@/types/events';
import { makeMapping, makeObjectMapping } from '@/utils/mapping';
import { objectToQueryString } from '@/utils/object';
import { BaseTracker } from './BaseTracker';

const logger = getLogger('FacebookTracker');

export class FacebookTracker extends BaseTracker {
  constructor() {
    super();

    this.scriptUrl = 'https://www.facebook.com/tr';
    this.trackerName = 'facebook';
    this.listeners = this.createStandardTrackingListeners();
  }

  async init() {
    try {
      if (this.isDisabled()) {
        this.state = 'disabled';
        return this;
      }
      this.state = 'ready';
    } catch (err) {
      logger.error('Facebook tracker initialization error', err);
      throw err;
    }

    return this;
  }

  async trackEvent(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const mappedEvent = makeMapping(this.trackerName, 'trackEvent', e.value);

    if (!mappedEvent) {
      logger.debug('Values not found in the mapping. Event tracking aborted', e.value);
      return;
    }

    const url = this.getUrl(mappedEvent.value, e);
    if (url) {
      // Do not track errors - it can have false positives.
      await fetch(url);
      logger.debug('Facebook event tracked', e.actionType, e.value, e.props);
    }
  }

  async trackPage(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const mappedPage = makeMapping(this.trackerName, 'trackPage', e.value);
    if (!mappedPage) {
      logger.debug('Values not found in the mapping. Page tracking aborted', e.value);
      return;
    }
    const pageName = decodeURIComponent(mappedPage.value);

    const url = this.getUrl(pageName, e);
    if (url) {
      // Do not track errors - it can have false positives.
      await fetch(url);
      logger.debug('Facebook page tracked', e.actionType, e.value, e.props);
    }
  }

  /**
   * Generates the URL for tracking by combining the base URL, tracking parameters and additional parameters passed in.
   */
  getUrl(eventName: string, params: Event) {
    const queryParams = {
      id: this.getToken(),
      ev: eventName,
    };

    const queryString = objectToQueryString(queryParams);
    const paramQuery = this.getUrlParams(params);

    return `${this.scriptUrl}?${queryString}${paramQuery}`;
  }

  /**
   * Get formatted URL params
   * Creates a mapping of custom params, and encoding them for use in a GET request
   */
  getUrlParams(params: Event) {
    const urlParams = Object.entries(makeObjectMapping(this.trackerName, params.props)).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        acc[encodeURIComponent(key)] = encodeURIComponent(value);
        return acc;
      },
      {}
    );

    return Object.entries(urlParams)
      .map(([key, value]) => `&cd[${key}]=${value}`)
      .join('');
  }
}
